/**
 * YouCam VTO Handler
 * Manages virtual try-on functionality using YMK SDK
 */

class VTOHandler {
  constructor() {
    this.config = window.youCamVTO || {};
    this.isInitialized = false;
    this.listeners = [];
    this.init();
    this.variantSku = "";
    this.type = "";
    this.selectedCarouselIndex = 0;
    this.recommendedSkus = [];
  }

  /**
   * Boots the handler once configuration indicates VTO is enabled.
   * Lazily waits for the YMK SDK before wiring events to avoid race conditions.
   */
  init() {
    if (!this.config.enabled) return;

    // Wait for YMK SDK to load
    this.waitForSDK().then(() => {
      this.setupEventListeners();
      this.registerYMKEvents();
      this.isInitialized = true;
    });
  }

  /**
   * Polls the global YMK object until its init function is available.
   * Returns a promise that resolves as soon as the SDK can be safely used.
   */
  waitForSDK() {
    return new Promise((resolve) => {
      const checkSDK = () => {
        if (window.YMK && typeof window.YMK.init === "function") {
          resolve();
        } else {
          setTimeout(checkSDK, 100);
        }
      };
      checkSDK();
    });
  }

  /**
   * Subscribes to every YMK makeup and shade-finder events we care about,
   * saving listener references for cleanup and delegating behavior to helpers.
   */
  registerYMKEvents() {
    // Listen to YMK events

    const openedListener = YMK.addEventListener("opened", () => {
      console.log("YMK loading event.");
      const slideshowContainer = document.querySelector('[ref="slideshow"]');
      const slideshowContainerPosition =
        slideshowContainer?.getBoundingClientRect();
      const YMKModule = document.getElementById("YMK-module");

      if (window.innerWidth < 750) {
        if (this.type === "foundation") {
          if (YMKModule && !YMKModule.classList.contains("vto-foundation")) {
            YMKModule.classList.add("vto-foundation");
          }
        } else {
          YMKModule.style.top = `${Math.abs(slideshowContainer?.offsetTop)}px`;

          setTimeout(() => {
            let YMKModuleHeight = YMKModule?.offsetHeight;

            if (document.getElementById("YMK-module-iframe")) {
              YMKModuleHeight =
                document.getElementById("YMK-module-iframe").offsetHeight;
            }
            YMKModule.style.setProperty("--YMK-height", `${YMKModuleHeight}px`);
            slideshowContainer.style.height = `${YMKModuleHeight}px`;
          }, 100);

          YMKModule.style.setProperty("--YMK-height", "480px");
          slideshowContainer.style.height = `480px`;
        }
      } else {
        YMKModule.style.left = `${slideshowContainerPosition?.left}px`;
        YMKModule.style.setProperty(
          "--YMK-height",
          `${slideshowContainerPosition?.height}px`,
        );
      }
      YMKModule.style.setProperty(
        "--YMK-width",
        `${slideshowContainerPosition?.width}px`,
      );
    });

    const loadedListener = YMK.addEventListener("loaded", () => {
      console.log("YMK loaded event");
      this.applyMakeupBySku(this.variantSku);
    });

    const closedListener = YMK.addEventListener("closed", () => {
      console.log("YMK closed event");
      this.closeShadeFinderPanel();
    });

    const shadeFinderCompleteListener = YMK.addEventListener(
      "shadeFinderComplete",
      (result) => {
        console.log("Shade finder complete:", result);
      },
    );

    const skinShadeFoundListener = YMK.addEventListener(
      "skinShadeFound",
      (result) => {
        console.log("Skin Shade Found:", result);
        this.handleShadeFinderResult(result);
      },
    );

    const shadeFinderStartedListenser = YMK.addEventListener(
      "shadeFinderStarted",
      () => {
        console.info("YMK Shade finder started event");
        this.closeShadeFinderPanel();
      },
    );

    const shadeFinderCancelledListener = YMK.addEventListener(
      "shadeFinderCancelled",
      () => {
        console.info("YMK Shade finder cancelled event");
        this.closeShadeFinderPanel();
      },
    );

    const cameraLoaded = YMK.addEventListener("cameraOpened", () => {
      console.info("YMK Camera opened event");
    });

    const cameraFailedListener = YMK.addEventListener("cameraFailed", () => {
      console.error("❌ Camera failed event.");
      alert(
        "Camera access is required for virtual try-on. Please enable camera permissions.",
      );
    });

    // Add error event listener
    const errorListener = YMK.addEventListener("error", (event) => {
      console.error("YMK error event:", event);
    });

    // Add module opened event
    const moduleOpenedListener = YMK.addEventListener(
      "moduleOpened",
      (event) => {
        console.log("Module opened event:", event);
      },
    );

    // Store listeners for cleanup
    this.listeners.push(
      openedListener,
      loadedListener,
      closedListener,
      shadeFinderStartedListenser,
      shadeFinderCompleteListener,
      skinShadeFoundListener,
      shadeFinderCancelledListener,
      cameraFailedListener,
      errorListener,
      moduleOpenedListener,
      cameraLoaded,
    );
  }

  /**
   * Binds DOM-level events for all VTO triggers, QR modal controls,
   * cross-window messaging, and panel UI interactions.
   */
  setupEventListeners() {
    const buttons = document.querySelectorAll("[data-vto-trigger]");

    buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        console.log("VTO button clicked:", button);

        this.type = button.dataset.vtoType;

        if (this.type === "foundation" && window.innerWidth >= 750) {
          const dialog = document.getElementById("vto-qr-dialog");
          dialog.showModal();
        } else {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
          this.openShadeFinder(button);
        }
      });
    });

    const qrModalClose = document.getElementById("qr-modal-close");
    qrModalClose?.addEventListener("click", () => {
      const dialog = document.getElementById("vto-qr-dialog");
      dialog.close();
    });

    document.addEventListener("vto:applyMakeupBySku", (event) => {
      this.variantSku = event.detail.sku;
      this.applyMakeupBySku(this.variantSku);
      buttons.forEach((button) => {
        button.setAttribute("data-variant-sku", this.variantSku);
      });
      console.info(this.variantSku);
    });

    document.addEventListener("vto:openShadeFinder", (event) => {
      this.type = event.detail.button.dataset.vtoType;
      if (this.type === "foundation" && window.innerWidth >= 750) {
        const dialog = document.getElementById("vto-qr-dialog");
        dialog.showModal();
      } else {
        this.openShadeFinder(event.detail.button);
      }
    });

    window.addEventListener("message", (event) => {
      // Process the message data
      const data = event.data;
      if (data.type === "vto:openShadeFinder") {
        const button = document.querySelector("[data-vto-trigger]");
        const openVTOEvent = new CustomEvent("vto:openShadeFinder", {
          detail: {
            button: button,
          },
        });
        document.dispatchEvent(openVTOEvent);
      }
    });

    const openShadeFinderOnMobileVTOParam = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("vto") === "true" && window.innerWidth < 750) {
        this.type = buttons[0].dataset.vtoType || "";
        // Only run on product detail pages (basic check: look for .product or .product-detail class)
        this.openShadeFinder(buttons[0]);
      }
    };

    // Auto-open shadefinder on mobile if vto=true in URL
    window.addEventListener("load", (event) => {
      openShadeFinderOnMobileVTOParam();
    });

    const YMKcontainer = document.getElementById("YMK-module-panel");
    if (YMKcontainer) {
      YMKcontainer.querySelector(
        "[data-vto-panel-all-shades]",
      ).addEventListener("click", (event) => {
        if (YMKcontainer.dataset.vtoPanelDisplay === "matches") {
          YMKcontainer.querySelectorAll(
            ".resource-list__slide.hidden",
          )?.forEach((slide) => {
            slide.classList.remove("hidden");
          });
          YMKcontainer.setAttribute("data-vto-panel-display", "all");
          YMKcontainer.querySelector(
            `.slideshow-control[style*='--slide-${Number(this.selectedCarouselIndex) + 1}']`,
          )?.click();
        }
      });
      YMKcontainer.querySelector(
        "[data-vto-panel-my-shade-matches]",
      ).addEventListener("click", (event) => {
        if (YMKcontainer.dataset.vtoPanelDisplay === "all") {
          YMKcontainer.setAttribute("data-vto-panel-display", "matches");
          this.showOnlyMatches(YMKcontainer);
        }
      });
      YMKcontainer.querySelectorAll("[type='radio']").forEach(
        (swatch, index) => {
          swatch.addEventListener("change", (event) => {
            if (!event.target) return;
            event.target.checked = true;
            this.swatchChangeHandler(
              YMKcontainer,
              event.target.dataset.variantSku,
            );
            this.selectedCarouselIndex = YMKcontainer.querySelectorAll(
              ".vto-swatch-list__slide",
            )[index].dataset.dotIndex;
          });
        },
      );
    }
  }

  /**
   * Hides or reveals swatch slides based on the captured recommendation set.
   * Optionally writes undertone labels when displayNames is true.
   * @param {HTMLElement} container - The container element.
   * @param {boolean} displayNames - If undertone labels should be written.
   */
  showOnlyMatches(container, displayNames = false) {
    if (this.recommendedSkus.length) {
      const slides = container.querySelectorAll(".resource-list__slide");

      slides.forEach((slide) => {
        const hasMatch = this.recommendedSkus.some((rec) =>
          slide.querySelector(`[data-variant-sku='${rec.sku}']`),
        );

        if (!hasMatch) {
          slide.classList.add("hidden");
          if (displayNames && slide.querySelector("[data-undertone-type]")) {
            slide.querySelector("[data-undertone-type]").textContent = "";
          }
        } else {
          slide.classList.remove("hidden");
          if (displayNames && slide.querySelector("[data-undertone-type]")) {
            const reco = this.recommendedSkus.find(
              (r) =>
                r.sku ===
                slide.querySelector("[data-variant-sku]").dataset.variantSku,
            );
            slide.querySelector("[data-undertone-type]").textContent =
              reco.type;
          }
        }
      });
    }
  }

  /**
   * Resets the panel UI and sticky ATC state when the shade finder closes.
   * Only applies on small screens where the module overlays the PDP.
   */
  closeShadeFinderPanel() {
    if (window.innerWidth < 750) {
      const slideshowContainer = document.querySelector('[ref="slideshow"]'),
        YMLCntr = document.getElementById("YMK-module-container"),
        addToCart = document.querySelector("add-to-cart[data-sticky]");
      slideshowContainer.style.height = "auto";

      if (addToCart && addToCart.classList.contains("sticky-atc")) {
        addToCart.classList.remove("sticky-atc");
      }

      if (YMLCntr && YMLCntr.classList.contains("vto-show-panel")) {
        YMLCntr.classList.remove("vto-show-panel");
      }
    }
  }

  /**
   * Ensures the recommendation panel is visible and the ATC sticks on screen.
   * Called once matches are ready so users can interact with them immediately.
   * @param {HTMLElement} container - The container element.
   */
  openShadeFinderPanel(container) {
    if (!container.classList.contains("vto-show-panel")) {
      container.classList.add("vto-show-panel");
      if (
        document.querySelector("add-to-cart[data-sticky]") &&
        !document
          .querySelector("add-to-cart[data-sticky]")
          .classList.contains("sticky-atc")
      ) {
        document
          .querySelector("add-to-cart[data-sticky]")
          ?.classList.add("sticky-atc");
      }
    }
  }

  /**
   * Syncs a SKU selection back into the core variant picker and panel heading.
   * Dispatches a change event so existing product logic stays in sync.
   * @param {HTMLElement} container - The container element.
   * @param {string} sku - The SKU to sync.
   */
  swatchChangeHandler(container, sku) {
    const variantPicker = document.querySelector("variant-picker");
    if (variantPicker) {
      const selectedSwatch = variantPicker.querySelector(
          `[type="radio"][data-variant-sku="${sku}"]`,
        ),
        changeEvent = new Event("change", { bubbles: true });

      if (selectedSwatch) {
        selectedSwatch?.dispatchEvent(changeEvent);

        container.querySelector("[data-vto-panel-shade-name").textContent =
          selectedSwatch.value;
      }
    }
  }

  /**
   * Opens the appropriate YMK experience (foundation vs. other modes)
   * based on the trigger element's metadata, storing the SKU for re-use.
   * @param {HTMLElement} trigger - The trigger element.
   */
  openShadeFinder(trigger) {
    if (!window.YMK) {
      console.error("YMK SDK not loaded");
      return;
    }

    try {
      if (this.type === "foundation") {
        window.YMK.open(true, null, true);
      } else {
        window.YMK.open();
      }
      this.variantSku = trigger?.dataset?.variantSku;
    } catch (error) {
      YMK.openShadeFinderDebug();
      console.error("Error opening shade finder:", error);
    }
  }

  /**
   * Delegates to the YMK SDK to render a specific SKU on the face canvas.
   * Guards against missing SDK availability to avoid hard crashes.
   * @param {string} skuId - The SKU ID to apply makeup for.
   */
  applyMakeupBySku(skuId) {
    if (!window.YMK) {
      console.error("YMK SDK not loaded");
      return;
    }

    YMK.applyMakeupBySku(skuId, () => {
      console.info("Makeup applied:", skuId);
    });
  }

  /**
   * Consumes shade finder results, auto-selects the best match,
   * filters the swatch carousel down to recommendations, and opens the panel.
   * @param {Object} result - The shade finder result.
   */
  handleShadeFinderResult(result) {
    // Handle shade finder results
    const { readableRecommendations } = result;

    console.log("Product recommendations:", readableRecommendations);

    // Optionally auto-apply the best matched product
    if (readableRecommendations && readableRecommendations.length > 0) {
      const recommendation = readableRecommendations[0];

      const bestMatch = {
          type: "Match",
          sku: recommendation?.guid,
        },
        cooler = {
          type: "Cooler",
          sku: recommendation?.cooler,
        },
        warmer = {
          type: "Warmer",
          sku: recommendation?.warmer,
        },
        darker = {
          type: "Darker",
          sku: recommendation?.darker,
        },
        lighter = {
          type: "Lighter",
          sku: recommendation?.lighter,
        };

      if (bestMatch) {
        console.log("Auto-applying best match:", bestMatch.sku);
        const container = document.getElementById("YMK-module-container");
        this.applyMakeupBySku(bestMatch.sku);

        if (container) {
          try {
            container.querySelector(
              `[type='radio'][data-variant-sku='${bestMatch.sku}']`,
            ).checked = true;
            this.swatchChangeHandler(container, bestMatch.sku);

            const matchedSkus = [
              lighter,
              cooler,
              bestMatch,
              warmer,
              darker,
            ].filter(Boolean);

            this.selectedCarouselIndex = container.querySelector(
              `.vto-swatch-list__slide[data-variant-sku='${bestMatch.sku}']`,
            ).dataset.dotIndex;

            this.recommendedSkus = matchedSkus;

            this.showOnlyMatches(container, true);
          } catch (error) {
            this.openShadeFinderPanel(container);
            console.error("Error applying makeup by SKU:", error);
          }
        }
      }
    }
  }

  /**
   * Unsubscribes all registered YMK listeners and closes the module gracefully.
   * Useful when tearing down the handler or navigating away.
   */
  close() {
    if (window.YMK) {
      YMK.close();
    }

    // Clean up event listeners
    this.listeners.forEach((listener) => {
      if (window.YMK) {
        YMK.removeEventListener(listener);
      }
    });
    this.listeners = [];
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.vtoHandler = new VTOHandler();
  });
} else {
  window.vtoHandler = new VTOHandler();
}
